/**
 * questionRegistry.js — engine metadata for every product-discovery question.
 *
 * The registry deliberately contains NO presentation copy — borrower-facing
 * wording lives with each UI flow (e.g. src/config/readinessQuestions.js).
 * Here each question declares:
 *
 *   key          answer key (matches readiness question ids where they exist)
 *   resolves     which unknown inputs (from eligibility missingInputs) it fills
 *   stage        core | eligibility | qualification | intent | conditional
 *   friction     1 (tap a chip) … 3 (requires thought/documents)
 *   values       enumerable canonical answers for elimination simulation
 *   simulate     (profile, value) → partial profile patch for what-if runs
 *   appliesWhen  (profile, ctx) → should this ever be asked for this borrower
 *
 * ctx = { remaining: evaluations still alive, armAlive, fixedTermsAlive }
 */

const STAGE_WEIGHT = { core: 3, eligibility: 2.4, qualification: 2, intent: 1.6, conditional: 1.2 };

export const QUESTION_REGISTRY = [
  {
    key: 'purpose', stage: 'core', friction: 1,
    resolves: ['loanPurpose'],
    values: ['purchase', 'refinance'],
    simulate: (p, v) => ({ loanPurpose: v }),
    appliesWhen: () => true,
  },
  {
    key: 'refiKind', stage: 'core', friction: 1,
    resolves: ['refiKind'],
    values: ['rate-term', 'cash-out'],
    simulate: (p, v) => ({ refiKind: v }),
    appliesWhen: (p) => p.loanPurpose === 'refinance',
  },
  {
    key: 'existingLoanType', stage: 'core', friction: 1,
    resolves: ['existingLoanType'],
    values: ['conventional', 'fha', 'va', 'usda', 'other'],
    simulate: (p, v) => ({ existingLoanType: v }),
    appliesWhen: (p) => p.loanPurpose === 'refinance',
  },
  {
    key: 'propertyUse', stage: 'core', friction: 1,
    resolves: ['occupancy'],
    values: ['primary', 'second-home', 'investment'],
    simulate: (p, v) => ({ occupancy: v }),
    appliesWhen: () => true,
  },
  {
    key: 'propertyType', stage: 'core', friction: 1,
    resolves: ['propertyType', 'units'],
    values: ['sfr', 'condo', 'townhouse', 'multi'],
    simulate: (p, v) => ({ propertyType: v, units: v === 'multi' ? 2 : 1 }),
    appliesWhen: () => true,
  },
  {
    key: 'value', stage: 'core', friction: 2,
    resolves: ['value', 'ltv', 'loan-size'],
    values: null, // currency — information value from missingInputs instead
    appliesWhen: () => true,
  },
  {
    key: 'down', stage: 'core', friction: 2,
    resolves: ['down', 'ltv', 'loan-size'],
    values: null,
    appliesWhen: () => true,
  },
  {
    key: 'credit', stage: 'core', friction: 1,
    resolves: ['credit'],
    values: ['760+', '700-759', '660-699', '600-659', '<600'],
    simulate: (p, v) => {
      const bands = {
        '760+': { min: 760, max: 850 }, '700-759': { min: 700, max: 759 },
        '660-699': { min: 660, max: 699 }, '600-659': { min: 600, max: 659 },
        '<600': { min: 300, max: 599 },
      };
      return { creditBand: bands[v] };
    },
    appliesWhen: () => true,
  },
  {
    key: 'military', stage: 'eligibility', friction: 1,
    resolves: ['veteranStatus'],
    values: ['yes', 'no'],
    simulate: (p, v) => ({ veteranStatus: v === 'yes' ? 'eligible' : 'not-eligible' }),
    appliesWhen: () => true,
  },
  {
    key: 'employment', stage: 'core', friction: 1,
    resolves: ['employment'],
    values: ['w2', 'self', 'retired', 'other'],
    simulate: (p, v) => ({ incomeType: v === 'w2' ? 'w2' : v === 'self' ? 'self-employed' : v }),
    appliesWhen: () => true,
  },
  {
    key: 'income', stage: 'qualification', friction: 2,
    resolves: ['income'],
    values: null,
    appliesWhen: () => true,
  },
  {
    key: 'debts', stage: 'qualification', friction: 2,
    resolves: ['debts'],
    values: null,
    appliesWhen: () => true,
  },
  {
    key: 'firstTime', stage: 'eligibility', friction: 1,
    resolves: ['firstTimeHomebuyer'],
    values: ['yes', 'no'],
    simulate: (p, v) => ({ firstTimeHomebuyer: v === 'yes' }),
    appliesWhen: (p) => p.loanPurpose === 'purchase',
  },
  {
    key: 'renovationNeeded', stage: 'eligibility', friction: 1,
    resolves: ['renovationNeeded'],
    values: ['yes', 'no'],
    simulate: (p, v) => ({ renovationNeeded: v === 'yes' }),
    appliesWhen: () => true,
  },
  {
    key: 'state', stage: 'eligibility', friction: 1,
    resolves: ['state'],
    values: ['ca', 'other'],
    simulate: (p, v) => ({ state: v === 'ca' ? 'CA' : 'other' }),
    appliesWhen: () => true,
  },
  {
    key: 'county', stage: 'eligibility', friction: 1,
    resolves: ['ruralEligibility', 'county'],
    values: null, // enumeration lives in the UI; rural derivation happens in the adapter
    appliesWhen: (p) => p.state === 'CA',
  },

  // ── Term-strategy (intent) questions — from Borrower Decision Questions ──
  {
    key: 'primaryMortgageGoal', stage: 'intent', friction: 1,
    resolves: ['primaryMortgageGoal'],
    values: ['lowest-payment', 'balanced', 'fast-payoff', 'build-equity', 'lowest-interest', 'flexibility'],
    simulate: (p, v) => ({ primaryMortgageGoal: v }),
    appliesWhen: () => true,
  },
  {
    key: 'expectedYearsInHome', stage: 'intent', friction: 1,
    resolves: ['expectedHoldYears'],
    values: ['<3', '3-5', '6-7', '8-10', '10+', 'unsure'],
    simulate: (p, v) => {
      const map = { '<3': 2, '3-5': 4, '6-7': 6.5, '8-10': 9, '10+': 15 };
      return { expectedHoldYears: map[v], expectedHoldAnswered: true };
    },
    appliesWhen: () => true,
  },
  {
    key: 'maxComfortablePayment', stage: 'intent', friction: 2,
    resolves: ['maxComfortablePayment'],
    values: null,
    appliesWhen: () => true,
  },
  {
    key: 'paymentStabilityPreference', stage: 'intent', friction: 1,
    resolves: ['paymentStabilityPreference'],
    values: ['essential', 'important', 'flexible', 'comfortable'],
    simulate: (p, v) => ({ paymentStabilityPreference: v }),
    // Workbook AskWhen: only matters if an ARM/IO product is still alive
    appliesWhen: (p, ctx) => ctx.armAlive,
  },
  {
    key: 'rateRiskTolerance', stage: 'conditional', friction: 1,
    resolves: ['rateRiskTolerance'],
    values: ['no', 'maybe', 'yes'],
    simulate: (p, v) => ({ rateRiskTolerance: v }),
    // Only if ARM survives AND the borrower's hold could fit an ARM window
    appliesWhen: (p, ctx) =>
      ctx.armAlive &&
      p.paymentStabilityPreference !== 'essential' &&
      (p.expectedHoldYears === undefined || p.expectedHoldYears <= 9),
  },
  {
    key: 'plannedExtraPayments', stage: 'conditional', friction: 1,
    resolves: ['plannedExtraPayments'],
    values: ['no', 'occasionally', 'monthly', 'large-periodic'],
    simulate: (p, v) => ({ plannedExtraPayments: v }),
    // Workbook AskWhen: if a 30-year fixed remains a candidate and payoff matters
    appliesWhen: (p, ctx) =>
      ctx.fixedTermsAlive.includes(30) &&
      (p.primaryMortgageGoal === 'fast-payoff' || p.primaryMortgageGoal === 'build-equity' || p.primaryMortgageGoal === 'lowest-interest'),
  },
];

export { STAGE_WEIGHT };

export function questionByKey(key) {
  return QUESTION_REGISTRY.find((q) => q.key === key) || null;
}
